import { Routes, Route, Navigate, useLocation } from "react-router-dom"
import { lazy, Suspense } from "react"
import { motion } from "framer-motion"
import { useAuth } from "@/hooks/useAuth"
import { useTenant } from "@/hooks/useTenant"
import type { AppRole } from "@/types/index"
import { podeEntrarNoGerenciamento } from "@/lib/allowlist"
import { mensalidadeNoArParaPapel } from "@/lib/mensalidade/liberacao"
import { enderecoDoPagamento } from "@/lib/mensalidade/endereco"
// Modulo Financeiro (task #18): abre por papel e pela chave de cada unidade.
import { PortaoDoFinanceiro } from "@/components/financeiro/ui/PortaoDoFinanceiro"

// ─── Lazy Pages ────────────────────────────────────────────────────────────────
const Home = lazy(() => import("@/pages/Home"))
const Login = lazy(() => import("@/pages/Login"))
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"))
const DefinirSenha = lazy(() => import("@/pages/DefinirSenha"))
const NewESC = lazy(() => import("@/pages/NewESC"))
const Funil = lazy(() => import("@/pages/Funil"))
const ConfigFunil = lazy(() => import("@/pages/configuracoes/ConfigFunil"))
const ConfigESC = lazy(() => import("@/pages/configuracoes/ConfigESC"))
const ConfigUsuarios = lazy(() => import("@/pages/configuracoes/ConfigUsuarios"))
const ConfigCaptacao = lazy(() => import("@/pages/configuracoes/ConfigCaptacao"))
const ConfigProduto = lazy(() => import("@/pages/configuracoes/ConfigProduto"))
const ConfigSDR = lazy(() => import("@/pages/configuracoes/ConfigSDR"))
const ConfigAutomacoes = lazy(() => import("@/pages/configuracoes/ConfigAutomacoes"))
const ConfigSOMA = lazy(() => import("@/pages/configuracoes/ConfigSOMA"))
const ConfigVendedoresSoma = lazy(() => import("@/pages/configuracoes/ConfigVendedoresSoma"))
const ConfigExportacoes = lazy(() => import("@/pages/configuracoes/ConfigExportacoes"))
// A mensalidade da unidade (Configurações → Pagamento), só no ensaio até o PM liberar.
const ConfigPagamento = lazy(() => import("@/pages/configuracoes/ConfigPagamento"))
const NovoLead = lazy(() => import("@/pages/NovoLead"))
const Notifications = lazy(() => import("@/pages/Notifications"))
const Anotacoes = lazy(() => import("@/pages/Anotacoes"))
const Suporte = lazy(() => import("@/pages/Suporte"))
const SuporteNovidades = lazy(() => import("@/pages/SuporteNovidades"))
const SuporteAjuda = lazy(() => import("@/pages/SuporteAjuda"))
const SuporteAjudaArtigo = lazy(() => import("@/pages/SuporteAjudaArtigo"))
const MeusFeedbacks = lazy(() => import("@/pages/MeusFeedbacks"))
const Parceiro4ESC = lazy(() => import("@/pages/parceiros/Parceiro4ESC"))
const Perfil = lazy(() => import("@/pages/Perfil"))
const SuperAdminDashboard = lazy(() => import("@/pages/super-admin/Dashboard"))
const ESCManagement = lazy(() => import("@/pages/super-admin/ESCManagement"))
const Dashboard = lazy(() => import("@/pages/Dashboard"))
const EnviarDocumentos = lazy(() => import("@/pages/EnviarDocumentos"))
const Mensagens = lazy(() => import("@/pages/Mensagens"))
const ImportacaoSoma = lazy(() => import("@/pages/ImportacaoSoma"))
const ImportarLeadsMeta = lazy(() => import("@/pages/ImportarLeadsMeta"))
const MapaClientes = lazy(() => import("@/pages/MapaClientes"))
const Cobrancas = lazy(() => import("@/pages/Cobrancas"))
const LandingPages = lazy(() => import("@/pages/LandingPages"))
const LpUnidade = lazy(() => import("@/pages/public/LpUnidade"))
const LpVendedor = lazy(() => import("@/pages/public/LpVendedor"))
const IVEDashboard = lazy(() => import("@/pages/admin/IVEDashboard"))
const CadastroCliente = lazy(() => import("@/pages/public/CadastroCliente"))
const MetaOAuthCallback = lazy(() => import("@/pages/oauth/MetaOAuthCallback"))
// ─── Modulo Financeiro (task #18) — cada aba vira um chunk lazy proprio ───────
const FinanceiroLayout = lazy(() => import("@/pages/financeiro/FinanceiroLayout"))
const FinanceiroDashboard = lazy(() => import("@/pages/financeiro/DashboardTab"))
const FinanceiroImportacao = lazy(() => import("@/pages/financeiro/ImportacaoTab"))
const FinanceiroDre = lazy(() => import("@/pages/financeiro/DreTab"))
const FinanceiroDestinacao = lazy(() => import("@/pages/financeiro/DestinacaoTab"))
const FinanceiroDespesas = lazy(() => import("@/pages/financeiro/DespesasTab"))
const FinanceiroReceitas = lazy(() => import("@/pages/financeiro/ReceitasTab"))
const FinanceiroInadimplencia = lazy(() => import("@/pages/financeiro/InadimplenciaTab"))

// ─── Layout components ────────────────────────────────────────────────────────
import { PublicLayout } from "@/components/layout/PublicLayout"
import { AppLayout } from "@/components/layout/AppLayout"
import { SettingsLayout } from "@/components/layout/SettingsLayout"

// ─── Loading fallback ─────────────────────────────────────────────────────────
function PageSkeleton() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <motion.div
        className="flex flex-col items-center gap-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        <motion.div
          className="w-10 h-10 rounded-full border-2 border-[var(--accent-blue,#2563eb)] border-t-transparent"
          animate={{ rotate: 360 }}
          transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
        />
        <p className="text-sm text-[var(--text-secondary,#64748b)]">Carregando...</p>
      </motion.div>
    </div>
  )
}

// ─── Load error fallback ──────────────────────────────────────────────────────
function LoadErrorScreen({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center space-y-4 max-w-md px-6">
        <p className="text-4xl">⚠️</p>
        <h2 className="text-xl font-semibold text-[var(--text-primary)]">
          Falha ao carregar
        </h2>
        <p className="text-sm text-[var(--text-secondary)]">
          Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.
        </p>
        <button
          onClick={onRetry}
          className="mt-4 px-6 py-2.5 rounded-xl text-sm font-medium bg-[var(--accent-blue,#2563eb)] text-white hover:opacity-90 transition-opacity"
        >
          Tentar novamente
        </button>
      </div>
    </div>
  )
}

// ─── Route Guards ─────────────────────────────────────────────────────────────
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  const { hasLoadError, retryLoad } = useTenant()
  const location = useLocation()

  if (isLoading) return <PageSkeleton />
  if (!isAuthenticated) {
    if (hasLoadError) return <LoadErrorScreen onRetry={retryLoad} />
    return <Navigate to="/login" state={{ from: location }} replace />
  }
  return <>{children}</>
}

function TenantRequired({ children }: { children: React.ReactNode }) {
  const { currentTenant, tenants, setCurrentTenant, isDataReady } = useTenant()
  const { isAuthenticated, isLoading, canAccess } = useAuth()

  // Wait for auth + tenant data to resolve before routing decisions
  if (isLoading) return <PageSkeleton />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!isDataReady) return <PageSkeleton />

  // Auto-select first tenant if none selected
  if (!currentTenant && tenants.length > 0) {
    setCurrentTenant(tenants[0])
    return <PageSkeleton />
  }

  // Authenticated but no tenants available
  if (!currentTenant) {
    // Can create ESC? Redirect to wizard. Otherwise show message.
    if (canAccess("tenant_admin")) {
      return <Navigate to="/nova-esc" replace />
    }
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center space-y-2">
          <h2 className="text-xl font-semibold text-[var(--text-primary)]">
            Nenhuma ESC disponível
          </h2>
          <p className="text-[var(--text-secondary)]">
            Você não possui acesso a nenhuma ESC. Contate o administrador.
          </p>
        </div>
      </div>
    )
  }
  return <>{children}</>
}

/**
 * Tranca de rota por papel.
 *
 * Duas formas, e a segunda não é luxo:
 *
 *   minRole="seller"                    — do vendedor pra cima (hierarquia)
 *   allow={["seller", "tenant_admin"]}  — exatamente estes papéis
 *
 * 🚨 POR QUE `allow` EXISTE. A regra que o PM fechou em 11/08/2026 NÃO é uma
 * hierarquia na Landing Page:
 *
 *   Landing Page .. agência SIM, vendedor NÃO, backoffice SIM
 *
 * Não é descuido: a Landing Page é peça de marketing, que a agência configura e
 * o vendedor não. Espremer isso num `minRole` obrigaria a escolher entre deixar
 * o vendedor entrar ou barrar a agência. O buraco do meio é a regra, então a
 * tranca precisa saber listar.
 *
 * (Mensagens e Novo Lead nasceram com esse mesmo formato numa primeira leitura
 * da tabela, que tirava o backoffice das duas. O PM devolveu o acesso no mesmo
 * dia — o backoffice atende cliente sim — e as duas voltaram a ser hierárquicas.
 * Fica o registro para ninguém "consertar" isso de volta.)
 */
/** Quem mexe na peça de marketing. Vendedor fica de fora; agência entra. */
const PECA_DE_MARKETING: AppRole[] = [
  "ad_agency",
  "backoffice",
  "tenant_admin",
  "network_manager",
  "super_admin",
]

function RoleGate({
  children,
  minRole,
  allow,
}: {
  children: React.ReactNode
  minRole?: AppRole
  allow?: AppRole[]
}) {
  const { canAccess, role } = useAuth()

  const permitido = allow
    ? role != null && (allow as string[]).includes(role)
    : minRole != null && canAccess(minRole)

  if (!permitido) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-4xl">🔒</p>
          <h2 className="text-xl font-semibold text-[var(--text-primary)]">
            Acesso negado
          </h2>
          <p className="text-[var(--text-secondary)]">
            Você não tem permissão para acessar esta área.
          </p>
        </div>
      </div>
    )
  }
  return <>{children}</>
}

/**
 * #222/#223 e D-B1: o portão do Gerenciamento abre para quem vê ao menos uma
 * aba: "Unidades" e "Calculadora de Preços" (super_admin + user_ids
 * autorizados) ou "Cobranças" (super_admin e network_manager, só com as telas
 * da mensalidade no ar). A regra mora em `lib/allowlist.ts`.
 */
function PortaoDoGerenciamento({ children }: { children: React.ReactNode }) {
  const { user, role } = useAuth()
  if (!podeEntrarNoGerenciamento(user?.id, role)) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-4xl">🔒</p>
          <h2 className="text-xl font-semibold text-[var(--text-primary)]">
            Acesso negado
          </h2>
          <p className="text-[var(--text-secondary)]">
            Você não tem permissão para acessar esta área.
          </p>
        </div>
      </div>
    )
  }
  return <>{children}</>
}

/**
 * As telas da mensalidade só existem no build ligado ao banco de ensaio (o
 * alfa), até o PM liberar (`src/lib/mensalidade/liberacao.ts`) — ou, fora
 * dele, só para quem opera a cobrança da rede (`super_admin` e
 * `network_manager`, pedido do PM de 07/10/2026). Para o resto, o endereço
 * digitado à mão cai no Painel, como qualquer endereço que não existe.
 *
 * A régua é a do banco, e não a do endereço do site: o beta e as urls de cada
 * publicação falam com o banco da produção (revisão T-078, SEG-B5b-001). O menu
 * usa a mesma régua, e a liberação é uma constante só.
 */
function SoComAMensalidadeNoAr({ children }: { children: React.ReactNode }) {
  const { role } = useAuth()
  if (!mensalidadeNoArParaPapel(role)) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

/**
 * O endereço antigo da mensalidade (`/mensalidade`) leva à página de Pagamento,
 * com o `?unidade=<id>` quando ele tem forma de uuid (D-B5). Quem decide a
 * unidade e o papel é a própria página de Pagamento.
 */
function ParaOPagamento() {
  const { search } = useLocation()
  return <Navigate to={enderecoDoPagamento(new URLSearchParams(search).get("unidade")?.trim())} replace />
}

/** O endereço da aba Gerenciamento → Cobranças. */
const ABA_DAS_COBRANCAS = "/super-admin/gerenciamento?aba=cobrancas"

function SmartRedirect() {
  const { isAuthenticated } = useAuth()
  return <Navigate to={isAuthenticated ? "/dashboard" : "/"} replace />
}

// ─── Router ────────────────────────────────────────────────────────────────────
export function AppRouter() {
  return (
    <Suspense fallback={<PageSkeleton />}>
      <Routes>
        {/* Home — Landing page pública */}
        <Route path="/" element={<Home />} />

        {/* Login — modal over Home (not inside PublicLayout) */}
        <Route path="/login" element={<Login />} />

        {/* Public routes (auth pages) */}
        <Route element={<PublicLayout />}>
          <Route path="/esqueci-senha" element={<ForgotPassword />} />
          <Route path="/definir-senha" element={<DefinirSenha />} />
        </Route>

        {/* OAuth callbacks (público — sem layout, sem auth requirement na rota
            mas a página em si checa session via supabase.auth.getSession()) */}
        <Route path="/oauth/meta/callback" element={<MetaOAuthCallback />} />

        {/* App — todas as rotas autenticadas com sidebar */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          {/* Rotas que precisam de tenant */}
          <Route path="/dashboard" element={<TenantRequired><Dashboard /></TenantRequired>} />
          <Route path="/funil" element={<TenantRequired><RoleGate minRole="seller"><Funil /></RoleGate></TenantRequired>} />
          <Route path="/novo-lead" element={<TenantRequired><RoleGate minRole="seller"><NovoLead /></RoleGate></TenantRequired>} />
          <Route path="/notificacoes" element={<TenantRequired><Notifications /></TenantRequired>} />
          {/* Anotações: SEM RoleGate de propósito, pela mesma razão do Suporte.
              O mural de "como fazer" serve quem executa — o vendedor é quem
              mais consulta. Quem pode EDITAR e APAGAR cada anotação é decidido
              pela RLS da migration 175, não por tranca de rota. */}
          <Route path="/anotacoes" element={<TenantRequired><Anotacoes /></TenantRequired>} />
          {/* Suporte: SEM RoleGate de propósito — suporte que só o administrador
              alcança não é suporte. Todo mundo que tem login pode pedir ajuda e
              ler as novidades. */}
          <Route path="/suporte" element={<TenantRequired><Suporte /></TenantRequired>} />
          <Route path="/suporte/novidades" element={<TenantRequired><SuporteNovidades /></TenantRequired>} />
          <Route path="/suporte/ajuda" element={<TenantRequired><SuporteAjuda /></TenantRequired>} />
          <Route path="/suporte/ajuda/:slug" element={<TenantRequired><SuporteAjudaArtigo /></TenantRequired>} />
          <Route path="/meus-feedbacks" element={<TenantRequired><MeusFeedbacks /></TenantRequired>} />
          <Route path="/perfil" element={<Perfil />} />
          {/* Parceiro: SEM RoleGate e SEM TenantRequired, de propósito. É uma
              página que apresenta um parceiro e leva a um site público — não
              mostra nada de nenhuma unidade, então exigir unidade selecionada
              só criaria um beco sem saída para quem chegasse nela sem unidade.
              E é link, não poder: quem tem login pode ler. */}
          <Route path="/parceiros/4esc" element={<Parceiro4ESC />} />
          <Route path="/mensagens" element={<TenantRequired><RoleGate minRole="seller"><Mensagens /></RoleGate></TenantRequired>} />
          <Route path="/importacao-soma" element={<TenantRequired><RoleGate minRole="backoffice"><ImportacaoSoma /></RoleGate></TenantRequired>} />
          <Route path="/importar-leads-meta" element={<TenantRequired><RoleGate minRole="backoffice"><ImportarLeadsMeta /></RoleGate></TenantRequired>} />
          <Route path="/mapa-clientes" element={<TenantRequired><RoleGate minRole="ad_agency"><MapaClientes /></RoleGate></TenantRequired>} />
          <Route path="/cobrancas" element={<TenantRequired><RoleGate minRole="seller"><Cobrancas /></RoleGate></TenantRequired>} />
          <Route path="/landing-pages" element={<TenantRequired><RoleGate allow={PECA_DE_MARKETING}><LandingPages /></RoleGate></TenantRequired>} />
          {/* A mensalidade da unidade mora em Configurações → Pagamento (D-B2):
              o endereço antigo leva até lá, com a unidade, atrás da mesma trava. */}
          <Route
            path="/mensalidade"
            element={
              <SoComAMensalidadeNoAr>
                <ParaOPagamento />
              </SoComAMensalidadeNoAr>
            }
          />
          {/* A cobrança da rede mora na aba Gerenciamento → Cobranças (D-B5): o
              endereço antigo leva até lá, atrás da mesma trava. Quem decide se a
              pessoa vê a aba é o portão do Gerenciamento. */}
          <Route
            path="/mensalidades-da-rede"
            element={
              <SoComAMensalidadeNoAr>
                <Navigate to={ABA_DAS_COBRANCAS} replace />
              </SoComAMensalidadeNoAr>
            }
          />

          {/* Settings — nested layout */}
          <Route
            path="/configuracoes"
            element={
              <TenantRequired>
                <RoleGate minRole="tenant_admin">
                  <SettingsLayout />
                </RoleGate>
              </TenantRequired>
            }
          >
            <Route index element={<Navigate to="/configuracoes/funil" replace />} />
            <Route path="funil" element={<ConfigFunil />} />
            <Route
              path="esc"
              element={
                <RoleGate minRole="tenant_admin">
                  <ConfigESC />
                </RoleGate>
              }
            />
            <Route
              path="usuarios"
              element={
                <RoleGate minRole="tenant_admin">
                  <ConfigUsuarios />
                </RoleGate>
              }
            />
            {/* Registro de exportações — quem baixou planilha da unidade.
                Mesmo mínimo de Usuários: é informação de quem responde pela ESC. */}
            <Route
              path="exportacoes"
              element={
                <RoleGate minRole="tenant_admin">
                  <ConfigExportacoes />
                </RoleGate>
              }
            />
            {/* A mensalidade do CreditOS da unidade: só quem administra a unidade,
                e quem opera a cobrança da rede (decisões do PM de 30/09/2026). A
                unidade é escolhida dentro da página (EscolhaDaUnidade), e o
                servidor confere o vínculo de novo: a tranca daqui é a régua do
                papel. Fora do ensaio, até o PM liberar, o Painel. */}
            <Route
              path="pagamento"
              element={
                <SoComAMensalidadeNoAr>
                  <RoleGate minRole="tenant_admin">
                    <ConfigPagamento />
                  </RoleGate>
                </SoComAMensalidadeNoAr>
              }
            />
            <Route
              path="captacao"
              element={
                <RoleGate minRole="ad_agency">
                  <ConfigCaptacao />
                </RoleGate>
              }
            />
            <Route
              path="produto"
              element={
                <RoleGate minRole="ad_agency">
                  <ConfigProduto />
                </RoleGate>
              }
            />
            <Route
              path="sdr"
              element={
                <RoleGate minRole="ad_agency">
                  <ConfigSDR />
                </RoleGate>
              }
            />
            <Route
              path="soma"
              element={
                <RoleGate minRole="tenant_admin">
                  <ConfigSOMA />
                </RoleGate>
              }
            />
            {/* Quem é quem no SOMA: liga o nome de vendedor que vem do SOMA a uma
                pessoa da equipe (wdwxndy2bm). Quem grava é administrador; a RLS
                de credit_v2_seller_aliases confere de novo. */}
            <Route
              path="vendedores-do-soma"
              element={
                <RoleGate minRole="tenant_admin">
                  <ConfigVendedoresSoma />
                </RoleGate>
              }
            />
            {/* Automações do Funil — task #15. tenant_admin only. */}
            <Route
              path="automacoes"
              element={
                <RoleGate minRole="tenant_admin">
                  <ConfigAutomacoes />
                </RoleGate>
              }
            />
          </Route>

          {/* Financeiro (task #18), layout aninhado no mesmo molde de
              /configuracoes. Quem entra: `@/lib/financeiro/acesso`, a mesma
              régua do item do menu. */}
          <Route
            path="/financeiro"
            element={
              <TenantRequired>
                <PortaoDoFinanceiro>
                  <FinanceiroLayout />
                </PortaoDoFinanceiro>
              </TenantRequired>
            }
          >
            <Route index element={<Navigate to="/financeiro/dashboard" replace />} />
            <Route path="dashboard" element={<FinanceiroDashboard />} />
            <Route path="importacoes" element={<FinanceiroImportacao />} />
            <Route path="dre" element={<FinanceiroDre />} />
            <Route path="destinacao" element={<FinanceiroDestinacao />} />
            <Route path="despesas" element={<FinanceiroDespesas />} />
            <Route path="receitas" element={<FinanceiroReceitas />} />
            <Route path="inadimplencia" element={<FinanceiroInadimplencia />} />
            <Route path="conciliacao" element={<Navigate to="/financeiro/importacoes" replace />} />
          </Route>

          {/* Rotas SEM tenant required */}
          <Route
            path="/nova-esc"
            element={
              <RoleGate minRole="tenant_admin">
                <NewESC />
              </RoleGate>
            }
          />
          <Route
            path="/super-admin"
            element={
              <RoleGate minRole="super_admin">
                <SuperAdminDashboard />
              </RoleGate>
            }
          />
          <Route
            path="/super-admin/gerenciamento"
            element={
              <PortaoDoGerenciamento>
                <ESCManagement />
              </PortaoDoGerenciamento>
            }
          />
          <Route
            path="/admin/ive-dashboard"
            element={
              <RoleGate minRole="tenant_admin">
                <IVEDashboard />
              </RoleGate>
            }
          />
        </Route>

        {/* Public: cadastro de novo cliente (admin + ESC) */}
        <Route path="/cadastro-cliente" element={<CadastroCliente />} />

        {/* Public: document upload via token */}
        <Route path="/enviar-documentos/:token" element={<EnviarDocumentos />} />

        {/* Public: Landing Page — unit-wide (no auth required) */}
        <Route path="/lp/:tenantSlug" element={<LpUnidade />} />

        {/* Public: Landing Page — vendedor (no auth required) */}
        <Route path="/lp/:tenantSlug/:sellerSlug" element={<LpVendedor />} />

        {/* Catch-all → Dashboard if authenticated, Home otherwise */}
        <Route path="*" element={<SmartRedirect />} />
      </Routes>
    </Suspense>
  )
}
